import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  FaPalette,
  FaDesktop,
  FaBoxOpen,
  FaChartLine,
  FaGraduationCap,
  FaCode,
  FaCloud,
  FaCheck,
  FaArrowRight,
  FaChevronDown,
} from "react-icons/fa";
import { services } from "../../../config/constants/navData";
import { SectionHeader, Reveal } from "../../../components/common";

const SERVICE_ICONS = [FaPalette, FaDesktop, FaBoxOpen, FaChartLine, FaGraduationCap, FaCode, FaCloud];

const ServicesSection = () => {
  const [selected, setSelected] = useState(0);
  const ActiveIcon = SERVICE_ICONS[selected % SERVICE_ICONS.length];
  const active = services[selected];

  return (
    <section className="py-16 md:py-24">
      <SectionHeader
        index="02"
        eyebrow="What we do"
        title={<>One studio, seven crafts. Pick a lane or take the whole road.</>}
        lede="Tap through our disciplines to see exactly what each one includes, then start with whichever your brand needs most."
        layout="split"
      />

      {/* Desktop explorer */}
      <div className="mt-10 hidden gap-5 lg:grid lg:grid-cols-[0.9fr_1.1fr] md:mt-14">
        <Reveal className="h-full">
          <div className="flex h-full flex-col gap-2" role="tablist" aria-label="Our services">
            {services.map((service, i) => {
              const Icon = SERVICE_ICONS[i % SERVICE_ICONS.length];
              const isActive = i === selected;
              return (
                <button
                  key={service.title}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  aria-controls="service-panel"
                  id={`service-tab-${i}`}
                  onClick={() => setSelected(i)}
                  className={`group flex items-center gap-4 rounded-2xl border px-5 py-4 text-left transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-designer-primary/50 ${
                    isActive
                      ? "border-designer-dark bg-designer-dark text-white shadow-[0_18px_40px_-20px_rgba(29,32,48,0.5)]"
                      : "border-designer-dark/10 bg-white text-designer-dark hover:border-designer-primary/40 hover:bg-red-50/40"
                  }`}
                >
                  <span
                    className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl transition-colors ${
                      isActive ? "bg-designer-primary text-white" : "bg-red-50 text-designer-primary"
                    }`}
                  >
                    <Icon className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-display text-[17px] font-bold leading-snug">
                      {service.title}
                    </span>
                    <span className={`block font-mono text-[11px] tracking-[0.22em] ${isActive ? "text-white/50" : "text-gray-400"}`}>
                      0{i + 1} / 0{services.length}
                    </span>
                  </span>
                  <FaArrowRight
                    className={`h-4 w-4 shrink-0 transition-all duration-300 ${
                      isActive ? "translate-x-0 text-designer-accent opacity-100" : "-translate-x-1 opacity-0 group-hover:translate-x-0 group-hover:opacity-60"
                    }`}
                    aria-hidden="true"
                  />
                </button>
              );
            })}
          </div>
        </Reveal>

        <Reveal delay={120} className="h-full">
          <div
            key={selected}
            id="service-panel"
            role="tabpanel"
            aria-labelledby={`service-tab-${selected}`}
            className="animate-fade-in flex h-full flex-col rounded-[2rem] border border-designer-dark/10 bg-[#FBFAF9] p-7 md:p-9"
          >
            <div className="flex items-center gap-4">
              <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-designer-primary text-white shadow-lg shadow-red-500/30">
                <ActiveIcon className="h-6 w-6" aria-hidden="true" />
              </span>
              <div>
                <p className="font-mono text-[11px] tracking-[0.22em] text-gray-400 uppercase">
                  Service 0{selected + 1}
                </p>
                <h3 className="font-display text-xl md:text-2xl font-bold text-designer-dark leading-tight">
                  {active.title}
                </h3>
              </div>
            </div>

            <p className="mt-5 leading-relaxed text-gray-600">{active.details.overview}</p>

            <ul className="mt-6 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
              {active.details.features.map((feature) => (
                <li
                  key={feature}
                  className="flex items-start gap-2.5 rounded-xl border border-designer-dark/[0.07] bg-white px-3.5 py-3 text-sm font-medium text-gray-700"
                >
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-designer-primary/10">
                    <FaCheck className="h-2.5 w-2.5 text-designer-primary" aria-hidden="true" />
                  </span>
                  {feature}
                </li>
              ))}
            </ul>

            <div className="mt-6 flex flex-wrap gap-2">
              {active.details.tags.split(" • ").map((tag) => (
                <span
                  key={tag}
                  className="rounded-md bg-designer-dark/[0.06] px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-gray-500"
                >
                  {tag}
                </span>
              ))}
            </div>

            <div className="mt-auto pt-8">
              <Link
                to="/contact"
                className="group inline-flex items-center gap-2 rounded-full bg-designer-primary px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-red-700"
              >
                Start a project
                <FaArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </Reveal>
      </div>

      {/* Mobile / tablet accordion */}
      <div className="mt-8 space-y-3 lg:hidden">
        {services.map((service, i) => {
          const Icon = SERVICE_ICONS[i % SERVICE_ICONS.length];
          const isOpen = i === selected;
          return (
            <Reveal key={service.title} delay={Math.min(i, 6) * 50}>
              <div
                className={`overflow-hidden rounded-2xl border transition-colors duration-300 ${
                  isOpen ? "border-designer-dark/20 bg-white shadow-[0_18px_40px_-24px_rgba(29,32,48,0.4)]" : "border-designer-dark/10 bg-white"
                }`}
              >
                <button
                  type="button"
                  onClick={() => setSelected(i)}
                  aria-expanded={isOpen}
                  aria-controls={`service-acc-${i}`}
                  className="flex w-full items-center gap-3.5 px-4 py-4 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-designer-primary/50"
                >
                  <span
                    className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${
                      isOpen ? "bg-designer-primary text-white" : "bg-red-50 text-designer-primary"
                    }`}
                  >
                    <Icon className="h-4 w-4" aria-hidden="true" />
                  </span>
                  <span className="min-w-0 flex-1 font-display text-[17px] font-bold leading-snug text-designer-dark">
                    {service.title}
                  </span>
                  <FaChevronDown
                    className={`h-4 w-4 shrink-0 text-gray-400 transition-transform duration-300 ${isOpen ? "rotate-180 text-designer-primary" : ""}`}
                    aria-hidden="true"
                  />
                </button>
                <div
                  id={`service-acc-${i}`}
                  className={`grid transition-all duration-300 ease-out ${isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}
                >
                  <div className="overflow-hidden">
                    <div className="border-t border-designer-dark/[0.07] px-4 pb-5 pt-4">
                      <p className="text-sm leading-relaxed text-gray-600">{service.details.overview}</p>
                      <ul className="mt-4 space-y-2">
                        {service.details.features.slice(0, 4).map((feature) => (
                          <li key={feature} className="flex items-start gap-2.5 text-sm font-medium text-gray-700">
                            <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-designer-primary/10">
                              <FaCheck className="h-2.5 w-2.5 text-designer-primary" aria-hidden="true" />
                            </span>
                            {feature}
                          </li>
                        ))}
                      </ul>
                      <Link
                        to="/contact"
                        className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-designer-primary"
                      >
                        <span className="link-sweep">Start a project</span>
                        <FaArrowRight className="h-3 w-3" aria-hidden="true" />
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </Reveal>
          );
        })}
      </div>
    </section>
  );
};

export default ServicesSection;
